(() => {
  'use strict';

  const ROOT_SELECTOR =
    '[data-seasonal-site-animation]';

  if (window.SeasonalSiteAnimationV2) {
    window.SeasonalSiteAnimationV2.initAll();
    return;
  }


  /* =====================================
     UTILITIES
  ===================================== */

  const sleep = (ms) =>
    new Promise((resolve) =>
      setTimeout(resolve, ms)
    );


  const mobile = () =>
    window.matchMedia(
      '(max-width: 749px)'
    ).matches;


  const reducedMotion = () =>
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;


  const designMode = () =>
    Boolean(
      window.Shopify &&
      Shopify.designMode
    );


  function storageGet(storage, key) {

    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }

  }


  function storageSet(
    storage,
    key,
    value
  ) {

    try {
      storage.setItem(key, value);
    } catch (error) {
      // Storage disabled - animation
      // will simply continue normally.
    }

  }


  function currentDateKey() {

    const date = new Date();

    return [
      date.getFullYear(),
      String(
        date.getMonth() + 1
      ).padStart(2, '0'),
      String(
        date.getDate()
      ).padStart(2, '0')
    ].join('-');

  }


  function random(min, max) {

    return (
      Math.random() *
      (max - min) +
      min
    );

  }


  /* =====================================
     CONFIG
  ===================================== */

  function getConfig(root) {

    const config =
      root.querySelector(
        '[data-spa-config]'
      );

    if (!config) {
      return null;
    }

    try {

      return JSON.parse(
        config.textContent
      );

    } catch (error) {

      console.error(
        'Seasonal animation JSON error:',
        error
      );

      return null;

    }

  }


  /* =====================================
     DATE CONTROL
  ===================================== */

  function parseDate(
    value,
    endOfDay = false
  ) {

    if (!value) {
      return null;
    }

    const suffix =
      endOfDay
        ? 'T23:59:59'
        : 'T00:00:00';

    const parsed =
      new Date(
        `${value}${suffix}`
      );

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return null;
    }

    return parsed;

  }


  function scheduleAllowed(config) {

    if (designMode()) {
      return true;
    }

    const now =
      new Date();

    const start =
      parseDate(
        config.startDate
      );

    const end =
      parseDate(
        config.endDate,
        true
      );

    if (
      start &&
      now < start
    ) {
      return false;
    }

    if (
      end &&
      now > end
    ) {
      return false;
    }

    return true;

  }


  /* =====================================
     DEVICE CONTROL
  ===================================== */

  function deviceAllowed(config) {

    if (
      mobile() &&
      !config.showMobile
    ) {
      return false;
    }

    if (
      !mobile() &&
      !config.showDesktop
    ) {
      return false;
    }

    return true;

  }


  /* =====================================
     SESSION / DAILY / ONCE
  ===================================== */

  function frequencyKey(config) {

    const campaign =
      config.campaignKey ||
      'seasonal';

    return (
      `spa-v2:${campaign}:` +
      config.frequency
    );

  }


  function frequencyAllowed(config) {

    if (designMode()) {
      return true;
    }

    const key =
      frequencyKey(config);

    switch (
      config.frequency
    ) {

      case 'session':

        return (
          storageGet(
            sessionStorage,
            key
          ) !== 'played'
        );


      case 'day':

        return (
          storageGet(
            localStorage,
            key
          ) !== currentDateKey()
        );


      case 'once':

        return (
          storageGet(
            localStorage,
            key
          ) !== 'played'
        );


      default:

        return true;

    }

  }


  function saveFrequency(config) {

    if (designMode()) {
      return;
    }

    const key =
      frequencyKey(config);

    switch (
      config.frequency
    ) {

      case 'session':

        storageSet(
          sessionStorage,
          key,
          'played'
        );

        break;


      case 'day':

        storageSet(
          localStorage,
          key,
          currentDateKey()
        );

        break;


      case 'once':

        storageSet(
          localStorage,
          key,
          'played'
        );

        break;

    }

  }


  /* =====================================
     IMAGE PRELOADING
  ===================================== */

  async function prepareImages(root) {

    const images =
      Array.from(
        root.querySelectorAll(
          'img[data-spa-src]'
        )
      );

    images.forEach(
      (image) => {

        const full =
          image.dataset.spaSrc;

        if (full) {
          image.src = full;
        }

      }
    );


    const promises =
      images.map(
        (image) => {

          if (image.decode) {

            return (
              image
                .decode()
                .catch(() => {})
            );

          }

          return Promise.resolve();

        }
      );


    await Promise.race([
      Promise.all(promises),
      sleep(1400)
    ]);

  }


  /* =====================================
     PREMIUM IMAGE KEYFRAMES
  ===================================== */

  function imageFrames(
    motion,
    rotation
  ) {

    const base =
      'translate(-50%, -50%)';


    switch (motion) {


      /* POP + DEPTH */

      case 'pop_spin':

        return [
          {
            opacity: 0,
            filter: 'blur(12px)',
            transform:
              `${base} scale(.12) rotate(-130deg)`
          },
          {
            opacity: 1,
            filter: 'blur(0px)',
            offset: 0.32,
            transform:
              `${base} scale(1.15) rotate(${rotation + 20}deg)`
          },
          {
            opacity: 1,
            offset: 0.62,
            transform:
              `${base} scale(.96) rotate(${rotation - 7}deg)`
          },
          {
            opacity: 1,
            offset: 0.82,
            transform:
              `${base} scale(1.02) rotate(${rotation}deg)`
          },
          {
            opacity: 0,
            transform:
              `${base} scale(.92) rotate(${rotation}deg)`
          }
        ];


      /* CURVED CINEMATIC FLIGHT */

      case 'cinematic_arc':

        return [
          {
            opacity: 0,
            filter: 'blur(8px)',
            transform:
              `translate(calc(-50% - 115vw), calc(-50% + 45vh))
               scale(.7)
               rotate(${rotation - 25}deg)`
          },
          {
            opacity: 1,
            filter: 'blur(1px)',
            offset: 0.18,
            transform:
              `translate(calc(-50% - 65vw), calc(-50% + 10vh))
               scale(.88)
               rotate(${rotation - 12}deg)`
          },
          {
            opacity: 1,
            filter: 'blur(0)',
            offset: 0.46,
            transform:
              `translate(-50%, calc(-50% - 12vh))
               scale(1.08)
               rotate(${rotation + 5}deg)`
          },
          {
            opacity: 1,
            offset: 0.72,
            transform:
              `translate(calc(-50% + 52vw), calc(-50% - 3vh))
               scale(.96)
               rotate(${rotation + 10}deg)`
          },
          {
            opacity: 0,
            filter: 'blur(7px)',
            transform:
              `translate(calc(-50% + 115vw), calc(-50% + 35vh))
               scale(.75)
               rotate(${rotation + 25}deg)`
          }
        ];


      /* MOUSE / RUNNING OBJECT */

      case 'chase_right':

        return [
          {
            opacity: 0,
            transform:
              `translate(calc(-50% - 115vw), -50%)
               scale(.88)`
          },
          {
            opacity: 1,
            offset: .09,
            transform:
              `translate(calc(-50% - 92vw), calc(-50% - 3px))
               scale(1)`
          },
          {
            opacity: 1,
            offset: .22,
            transform:
              `translate(calc(-50% - 65vw), calc(-50% - 16px))
               rotate(${rotation + 4}deg)`
          },
          {
            opacity: 1,
            offset: .36,
            transform:
              `translate(calc(-50% - 35vw), calc(-50% + 5px))
               rotate(${rotation - 3}deg)`
          },
          {
            opacity: 1,
            offset: .51,
            transform:
              `translate(calc(-50% - 5vw), calc(-50% - 18px))
               rotate(${rotation + 4}deg)`
          },
          {
            opacity: 1,
            offset: .67,
            transform:
              `translate(calc(-50% + 35vw), calc(-50% + 5px))
               rotate(${rotation - 3}deg)`
          },
          {
            opacity: 1,
            offset: .83,
            transform:
              `translate(calc(-50% + 75vw), calc(-50% - 13px))
               rotate(${rotation + 4}deg)`
          },
          {
            opacity: 0,
            transform:
              `translate(calc(-50% + 115vw), -50%)
               rotate(${rotation}deg)`
          }
        ];


      /* ZIG-ZAG */

      case 'zigzag':

        return [
          {
            opacity: 0,
            transform:
              `translate(calc(-50% - 100vw), calc(-50% + 30vh))
               scale(.8)`
          },
          {
            opacity: 1,
            offset: .15,
            transform:
              `translate(calc(-50% - 65vw), calc(-50% - 20vh))
               rotate(${rotation + 12}deg)`
          },
          {
            opacity: 1,
            offset: .35,
            transform:
              `translate(calc(-50% - 20vw), calc(-50% + 15vh))
               rotate(${rotation - 12}deg)`
          },
          {
            opacity: 1,
            offset: .55,
            transform:
              `translate(calc(-50% + 20vw), calc(-50% - 20vh))
               rotate(${rotation + 12}deg)`
          },
          {
            opacity: 1,
            offset: .78,
            transform:
              `translate(calc(-50% + 65vw), calc(-50% + 18vh))
               rotate(${rotation - 10}deg)`
          },
          {
            opacity: 0,
            transform:
              `translate(calc(-50% + 110vw), calc(-50% - 20vh))
               rotate(${rotation + 8}deg)`
          }
        ];


      /* ORBIT */

      case 'orbit':

        return [
          {
            opacity: 0,
            transform:
              `${base}
               translateX(130px)
               scale(.75)
               rotate(${rotation}deg)`
          },
          {
            opacity: 1,
            offset: .15,
            transform:
              `${base}
               translate(80px, 90px)
               scale(.95)
               rotate(${rotation + 90}deg)`
          },
          {
            opacity: 1,
            offset: .4,
            transform:
              `${base}
               translate(-100px, 60px)
               scale(1.05)
               rotate(${rotation + 180}deg)`
          },
          {
            opacity: 1,
            offset: .65,
            transform:
              `${base}
               translate(-85px, -80px)
               scale(.95)
               rotate(${rotation + 270}deg)`
          },
          {
            opacity: 1,
            offset: .85,
            transform:
              `${base}
               translate(90px, -65px)
               scale(1)
               rotate(${rotation + 340}deg)`
          },
          {
            opacity: 0,
            transform:
              `${base}
               translateX(130px)
               scale(.8)
               rotate(${rotation + 360}deg)`
          }
        ];


      /* PREMIUM BOUNCE */

      case 'bounce':

        return [
          {
            opacity: 0,
            filter: 'blur(8px)',
            transform:
              `${base}
               translateY(140px)
               scale(.65)`
          },
          {
            opacity: 1,
            filter: 'blur(0)',
            offset: .28,
            transform:
              `${base}
               translateY(-38px)
               scale(1.08)`
          },
          {
            opacity: 1,
            offset: .46,
            transform:
              `${base}
               translateY(12px)
               scale(.96)`
          },
          {
            opacity: 1,
            offset: .62,
            transform:
              `${base}
               translateY(-16px)
               scale(1.02)`
          },
          {
            opacity: 1,
            offset: .78,
            transform:
              `${base}
               translateY(2px)
               scale(1)`
          },
          {
            opacity: 0,
            transform:
              `${base}
               translateY(-10px)
               scale(.96)`
          }
        ];


      /* FLOAT */

      case 'float':

        return [
          {
            opacity: 0,
            transform:
              `${base}
               translateY(65px)
               scale(.9)
               rotate(${rotation - 6}deg)`
          },
          {
            opacity: 1,
            offset: .22,
            transform:
              `${base}
               translateY(0)
               scale(1)
               rotate(${rotation}deg)`
          },
          {
            opacity: 1,
            offset: .5,
            transform:
              `${base}
               translate(-12px, -18px)
               rotate(${rotation + 5}deg)`
          },
          {
            opacity: 1,
            offset: .75,
            transform:
              `${base}
               translate(10px, -7px)
               rotate(${rotation - 4}deg)`
          },
          {
            opacity: 0,
            transform:
              `${base}
               translateY(-60px)
               rotate(${rotation + 3}deg)`
          }
        ];


      /* FALLING OBJECT */

      case 'fall':

        return [
          {
            opacity: 0,
            transform:
              `translate(-50%, calc(-50% - 120vh))
               rotate(${rotation - 160}deg)
               scale(.7)`
          },
          {
            opacity: 1,
            offset: .15,
            transform:
              `translate(-50%, calc(-50% - 75vh))
               rotate(${rotation - 100}deg)`
          },
          {
            opacity: 1,
            offset: .7,
            transform:
              `translate(-50%, calc(-50% + 60vh))
               rotate(${rotation + 100}deg)`
          },
          {
            opacity: 0,
            transform:
              `translate(-50%, calc(-50% + 125vh))
               rotate(${rotation + 180}deg)
               scale(.8)`
          }
        ];


      /* RISING OBJECT */

      case 'rise':

        return [
          {
            opacity: 0,
            transform:
              `translate(-50%, calc(-50% + 120vh))
               scale(.65)`
          },
          {
            opacity: 1,
            offset: .18,
            transform:
              `translate(-50%, calc(-50% + 70vh))
               scale(.9)`
          },
          {
            opacity: 1,
            offset: .7,
            transform:
              `translate(-50%, calc(-50% - 55vh))
               scale(1.05)`
          },
          {
            opacity: 0,
            transform:
              `translate(-50%, calc(-50% - 120vh))
               scale(.75)`
          }
        ];


      /* SWING */

      case 'swing':

        return [
          {
            opacity: 0,
            transform:
              `${base}
               rotate(${rotation - 30}deg)
               scale(.8)`
          },
          {
            opacity: 1,
            offset: .2,
            transform:
              `${base}
               rotate(${rotation + 18}deg)
               scale(1.02)`
          },
          {
            opacity: 1,
            offset: .42,
            transform:
              `${base}
               rotate(${rotation - 14}deg)`
          },
          {
            opacity: 1,
            offset: .62,
            transform:
              `${base}
               rotate(${rotation + 8}deg)`
          },
          {
            opacity: 1,
            offset: .8,
            transform:
              `${base}
               rotate(${rotation - 3}deg)`
          },
          {
            opacity: 0,
            transform:
              `${base}
               rotate(${rotation}deg)
               scale(.96)`
          }
        ];


      /* CINEMATIC PERSPECTIVE */

      case 'zoom_past':

        return [
          {
            opacity: 0,
            filter: 'blur(12px)',
            transform:
              `${base}
               scale(.08)
               rotate(${rotation - 15}deg)`
          },
          {
            opacity: 1,
            filter: 'blur(0)',
            offset: .35,
            transform:
              `${base}
               scale(.75)
               rotate(${rotation}deg)`
          },
          {
            opacity: 1,
            offset: .55,
            transform:
              `${base}
               scale(1.05)
               rotate(${rotation + 3}deg)`
          },
          {
            opacity: 0,
            filter: 'blur(10px)',
            transform:
              `${base}
               scale(3.4)
               rotate(${rotation + 12}deg)`
          }
        ];


      /* PEEK */

      case 'peek_left':

        return [
          {
            opacity: 0,
            transform:
              `translate(calc(-50% - 45vw), -50%)
               rotate(${rotation - 12}deg)`
          },
          {
            opacity: 1,
            offset: .28,
            transform:
              `translate(calc(-50% - 12vw), -50%)
               rotate(${rotation + 5}deg)`
          },
          {
            opacity: 1,
            offset: .65,
            transform:
              `translate(calc(-50% - 8vw), -50%)
               rotate(${rotation}deg)`
          },
          {
            opacity: 0,
            transform:
              `translate(calc(-50% - 50vw), -50%)
               rotate(${rotation - 15}deg)`
          }
        ];


      default:

        return [
          {
            opacity: 0
          },
          {
            opacity: 1,
            offset: .2
          },
          {
            opacity: 1,
            offset: .8
          },
          {
            opacity: 0
          }
        ];

    }

  }


  /* =====================================
     IMAGE ANIMATION
  ===================================== */

  function animateImage(
    root,
    item
  ) {

    const element =
      root.querySelector(
        `[data-spa-id="${item.id}"]`
      );

    if (!element) {
      return;
    }

    if (
      mobile() &&
      item.hideMobile
    ) {
      return;
    }

    const duration =
      Number(
        item.duration || 2
      ) * 1000;

    const delay =
      Number(
        item.delay || 0
      ) * 1000;

    const repeat =
      Math.max(
        1,
        Number(item.repeat || 1)
      );

    element.animate(
      imageFrames(
        item.motion,
        Number(
          item.rotation || 0
        )
      ),
      {
        duration,
        delay,
        iterations: repeat,
        fill: 'both',
        easing:
          'cubic-bezier(.16,1,.3,1)'
      }
    );

  }


  /* =====================================
     PARTICLES
  ===================================== */

  function chooseColor(item) {

    const colors = [
      item.color1,
      item.color2,
      item.color3
    ].filter(Boolean);

    return (
      colors[
        Math.floor(
          Math.random() *
          colors.length
        )
      ] ||
      '#ffffff'
    );

  }


  function createParticle(item) {

    const particle =
      document.createElement(
        'span'
      );

    particle.className =
      `spa-particle spa-particle--${item.particleStyle}`;

    const baseSize =
      Number(
        item.size || 8
      );

    const size =
      baseSize *
      random(.55, 1.5);

    particle.style.width =
      `${size}px`;

    particle.style.height =
      item.particleStyle ===
      'petal'
        ? `${size * 1.55}px`
        : item.particleStyle ===
          'confetti'
        ? `${size * 1.8}px`
        : `${size}px`;

    const color =
      chooseColor(item);

    particle.style.background =
      color;

    particle.style.color =
      color;

    return particle;

  }


  function particleFrames(
    item,
    spread
  ) {

    const angle =
      random(
        0,
        Math.PI * 2
      );

    const distance =
      random(
        spread * .25,
        spread
      );

    const x =
      Math.cos(angle) *
      distance;

    const y =
      Math.sin(angle) *
      distance;

    const rotate =
      random(-420, 420);


    switch (
      item.movement
    ) {

      case 'rain':

        return [
          {
            opacity: 0,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                -65vh
              )
              rotate(0deg)
              scale(.6)`
          },
          {
            opacity: 1,
            offset: .15
          },
          {
            opacity: 1,
            offset: .8,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                65vh
              )
              rotate(${rotate}deg)
              scale(1)`
          },
          {
            opacity: 0,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                85vh
              )
              rotate(${rotate + 80}deg)`
          }
        ];


      case 'rise':

        return [
          {
            opacity: 0,
            transform:
              `translate(
                ${random(-spread / 2, spread / 2)}px,
                30vh
              )
              scale(.4)`
          },
          {
            opacity: 1,
            offset: .2
          },
          {
            opacity: .8,
            offset: .75,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                -55vh
              )
              scale(1.1)`
          },
          {
            opacity: 0,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                -75vh
              )
              scale(.7)`
          }
        ];


      case 'drift':

        return [
          {
            opacity: 0,
            transform:
              `translate(
                ${random(-50, 50)}px,
                ${random(20, 80)}px
              )
              scale(.5)`
          },
          {
            opacity: 1,
            offset: .2
          },
          {
            opacity: .9,
            offset: .65,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                ${random(-spread / 2, spread / 2)}px
              )
              rotate(${rotate / 2}deg)`
          },
          {
            opacity: 0,
            transform:
              `translate(
                ${random(-spread, spread)}px,
                ${random(-spread, spread)}px
              )
              rotate(${rotate}deg)
              scale(.6)`
          }
        ];


      case 'burst':
      default:

        return [
          {
            opacity: 0,
            transform:
              'translate(-50%, -50%) scale(.1)'
          },
          {
            opacity: 1,
            offset: .12,
            transform:
              'translate(-50%, -50%) scale(1.2)'
          },
          {
            opacity: .95,
            offset: .52,
            transform:
              `translate(
                calc(-50% + ${x * .55}px),
                calc(-50% + ${y * .55}px)
              )
              rotate(${rotate / 2}deg)
              scale(1)`
          },
          {
            opacity: 0,
            transform:
              `translate(
                calc(-50% + ${x}px),
                calc(-50% + ${y}px)
              )
              rotate(${rotate}deg)
              scale(.5)`
          }
        ];

    }

  }


  function animateParticles(
    root,
    item
  ) {

    const host =
      root.querySelector(
        `[data-spa-id="${item.id}"]`
      );

    if (!host) {
      return;
    }

    if (
      mobile() &&
      item.hideMobile
    ) {
      return;
    }


    let quantity =
      Number(
        item.quantity || 18
      );


    if (mobile()) {

      quantity =
        Math.max(
          5,
          Math.round(
            quantity * .55
          )
        );

    }


    const spread =
      Number(
        item.spread || 350
      );


    const delay =
      Number(
        item.delay || 0
      ) * 1000;


    const duration =
      Number(
        item.duration || 2
      ) * 1000;


    for (
      let i = 0;
      i < quantity;
      i++
    ) {

      const particle =
        createParticle(item);

      host.appendChild(
        particle
      );


      particle.animate(
        particleFrames(
          item,
          spread
        ),
        {
          duration:
            duration *
            random(.75, 1.3),

          delay:
            delay +
            random(0, 350),

          fill: 'both',

          easing:
            'cubic-bezier(.12,.74,.22,1)'
        }
      );

    }

  }


  /* =====================================
     MESSAGE
  ===================================== */

  function messageFrames(type) {

    switch (type) {

      case 'zoom':

        return [
          {
            opacity: 0,
            filter: 'blur(10px)',
            transform:
              'translate(-50%, -50%) scale(.65)'
          },
          {
            opacity: 1,
            filter: 'blur(0)',
            offset: .3,
            transform:
              'translate(-50%, -50%) scale(1.04)'
          },
          {
            opacity: 1,
            offset: .72,
            transform:
              'translate(-50%, -50%) scale(1)'
          },
          {
            opacity: 0,
            filter: 'blur(5px)',
            transform:
              'translate(-50%, -50%) scale(1.12)'
          }
        ];


      case 'fade_up':

        return [
          {
            opacity: 0,
            transform:
              'translate(-50%, -25%)'
          },
          {
            opacity: 1,
            offset: .25,
            transform:
              'translate(-50%, -50%)'
          },
          {
            opacity: 1,
            offset: .75
          },
          {
            opacity: 0,
            transform:
              'translate(-50%, -70%)'
          }
        ];


      case 'cinematic':
      default:

        return [
          {
            opacity: 0,
            filter: 'blur(8px)',
            transform:
              'translate(-50%, -35%) scale(.94)'
          },
          {
            opacity: 1,
            filter: 'blur(0)',
            offset: .3,
            transform:
              'translate(-50%, -50%) scale(1.025)'
          },
          {
            opacity: 1,
            offset: .72,
            transform:
              'translate(-50%, -50%) scale(1)'
          },
          {
            opacity: 0,
            filter: 'blur(4px)',
            transform:
              'translate(-50%, -62%) scale(.98)'
          }
        ];

    }

  }


  function animateMessage(
    root,
    config
  ) {

    const message =
      root.querySelector(
        '[data-spa-message]'
      );

    if (!message) {
      return;
    }


    message.animate(
      messageFrames(
        config.message?.motion
      ),
      {
        duration:
          Number(
            config.message?.duration ||
            3
          ) * 1000,

        delay:
          Number(
            config.message?.delay ||
            0
          ) * 1000,

        easing:
          'cubic-bezier(.16,1,.3,1)',

        fill: 'both'
      }
    );

  }


  /* =====================================
     TOTAL DURATION
  ===================================== */

  function totalDuration(config) {

    let max =
      Number(
        config.message?.delay || 0
      ) +
      Number(
        config.message?.duration || 0
      );


    config.items.forEach(
      (item) => {

        let end =
          Number(
            item.delay || 0
          ) +
          Number(
            item.duration || 0
          );

        if (
          item.type === 'image'
        ) {

          end =
            Number(
              item.delay || 0
            ) +
            Number(
              item.duration || 0
            ) *
            Math.max(
              1,
              Number(
                item.repeat || 1
              )
            );

        }


        max =
          Math.max(
            max,
            end
          );

      }
    );


    return (
      Math.max(
        max,
        1.5
      ) *
      1000
    );

  }


  /* =====================================
     INIT
  ===================================== */

  async function init(root) {

    if (!root) {
      return;
    }


    if (
      root.dataset.spaReady ===
      'true'
    ) {
      return;
    }


    root.dataset.spaReady =
      'true';


    const config =
      getConfig(root);


    if (!config) {
      return;
    }


    if (
      !designMode() &&
      reducedMotion()
    ) {
      return;
    }


    if (
      !scheduleAllowed(config)
    ) {
      return;
    }


    if (
      !deviceAllowed(config)
    ) {
      return;
    }


    if (
      !frequencyAllowed(config)
    ) {
      return;
    }


    await prepareImages(root);


    root.classList.add(
      'is-active'
    );


    saveFrequency(config);


    animateMessage(
      root,
      config
    );


    config.items.forEach(
      (item) => {

        if (
          item.type === 'image'
        ) {

          animateImage(
            root,
            item
          );

        }


        if (
          item.type === 'particles'
        ) {

          animateParticles(
            root,
            item
          );

        }

      }
    );


    const total =
      totalDuration(config);


    window.setTimeout(
      () => {

        root.classList.add(
          'is-leaving'
        );

      },
      Math.max(
        0,
        total - 350
      )
    );


    window.setTimeout(
      () => {

        root.classList.remove(
          'is-active'
        );

        root.classList.add(
          'is-finished'
        );

      },
      total
    );

  }


  function initAll() {

    document
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(init);

  }


  window.SeasonalSiteAnimationV2 = {
    init,
    initAll
  };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );

  } else {

    initAll();

  }


  /* THEME EDITOR SUPPORT */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      const root =
        event.target
          .querySelector?.(
            ROOT_SELECTOR
          ) ||
        (
          event.target.matches?.(
            ROOT_SELECTOR
          )
            ? event.target
            : null
        );


      if (!root) {
        return;
      }


      root.dataset.spaReady =
        'false';


      init(root);

    }
  );

})();